class NotificationService {
    constructor() {
        this.permission = null;
        this.isSupported = 'Notification' in window;
        this.preferences = this.loadPreferences();
        this.init();
    }

    async init() {
        if (!this.isSupported) {
            console.warn('Browser notifications are not supported');
            return;
        }

        this.permission = Notification.permission;
        
        // Auto-request permission if not determined
        if (this.permission === 'default') {
            await this.requestPermission();
        }
    }

    async requestPermission() {
        if (!this.isSupported) {
            return false;
        }

        try {
            const permission = await Notification.requestPermission();
            this.permission = permission;
            return permission === 'granted';
        } catch (error) {
            console.error('Error requesting notification permission:', error);
            return false;
        }
    }

    canSendNotifications() {
        return this.isSupported && this.permission === 'granted';
    }

    sendPriceAlert(alertData) {
        if (!this.canSendNotifications()) {
            console.warn('Cannot send notifications - permission not granted');
            return null;
        }

        const {
            tokenSymbol,
            tokenName,
            alertType,
            currentPrice,
            targetPrice,
            percentageThreshold,
            triggerReason
        } = alertData;

        let title = '';
        let body = '';
        let icon = '/favicon.ico'; // Default icon

        switch (alertType) {
            case 'price_above':
                title = `🚀 ${tokenSymbol} Price Alert`;
                body = `${tokenSymbol} has reached $${currentPrice.toFixed(6)} (target: $${targetPrice.toFixed(6)})`;
                break;
            case 'price_below':
                title = `📉 ${tokenSymbol} Price Alert`;
                body = `${tokenSymbol} has dropped to $${currentPrice.toFixed(6)} (target: $${targetPrice.toFixed(6)})`;
                break;
            case 'percentage_change':
                title = `📊 ${tokenSymbol} Price Alert`;
                body = `${tokenSymbol} price changed by ${percentageThreshold}% - now $${currentPrice.toFixed(6)}`;
                break;
            default:
                title = `🔔 ${tokenSymbol} Alert`;
                body = triggerReason || 'Price alert triggered';
        }

        try {
            const notification = new Notification(title, {
                body,
                icon,
                badge: icon,
                tag: `price-alert-${alertData.alertId}`, // Prevent duplicate notifications
                requireInteraction: true, // Keep notification visible until user interacts
                data: {
                    alertId: alertData.alertId,
                    tokenAddress: alertData.tokenAddress,
                    tokenSymbol,
                    alertType,
                    timestamp: Date.now()
                }
            });

            // Handle notification click
            notification.onclick = () => {
                window.focus(); // Focus the browser window
                
                // Navigate to token or trading page
                if (window.location.pathname !== '/') {
                    window.location.href = `/?token=${alertData.tokenAddress}`;
                }
                
                notification.close();
            };

            // Auto-close after 10 seconds if not interacted with
            setTimeout(() => {
                notification.close();
            }, 10000);

            return notification;

        } catch (error) {
            console.error('Error sending notification:', error);
            return null;
        }
    }

    // Trending token notifications
    sendTrendingTokenAlert(alertData) {
        if (!this.canSendNotifications() || !this.shouldSendNotification('trending', 'trending_token_alert')) {
            return null;
        }

        const {
            tokenAddress,
            symbol,
            name,
            trendScore,
            volumeSpike24h,
            priceMovement1h,
            detectionSource,
            requireInteraction = false
        } = alertData;

        const displayName = symbol || name || 'Unknown Token';
        const title = `🔥 Trending Token Alert`;
        let body = `${displayName} is trending with score ${trendScore}`;
        
        if (volumeSpike24h) {
            body += ` (${volumeSpike24h.toFixed(0)}% volume spike)`;
        }

        try {
            const notification = new Notification(title, {
                body,
                icon: '/favicon.ico',
                badge: '/favicon.ico',
                tag: `trending-${tokenAddress}`,
                requireInteraction: requireInteraction || trendScore >= 80,
                data: {
                    category: 'trending',
                    tokenAddress,
                    symbol,
                    name,
                    trendScore,
                    volumeSpike24h,
                    priceMovement1h,
                    detectionSource,
                    timestamp: Date.now()
                }
            });

            notification.onclick = () => {
                window.focus();
                // Navigate to token discovery page with this token highlighted
                window.location.href = `/?tab=trending&token=${tokenAddress}`;
                notification.close();
            };

            // Auto-close based on trend score (higher scores stay longer)
            const autoCloseTime = trendScore >= 80 ? 15000 : 10000;
            setTimeout(() => {
                notification.close();
            }, autoCloseTime);

            return notification;

        } catch (error) {
            console.error('Error sending trending token notification:', error);
            return null;
        }
    }

    // Price target execution notifications
    sendPriceTargetExecutionNotification(executionData) {
        if (!this.canSendNotifications() || !this.shouldSendNotification('trading', 'price_target_executed')) {
            return null;
        }

        const {
            targetType,
            tokenAddress,
            tokenSymbol,
            targetPrice,
            executionPrice,
            amount,
            transactionHash,
            gasFee
        } = executionData;

        const actionText = targetType === 'buy' ? 'Bought' : 'Sold';
        const title = `✅ Price Target Executed`;
        const body = `${actionText} ${amount} ${tokenSymbol || 'tokens'} at ${executionPrice} (target: ${targetPrice})`;

        try {
            const notification = new Notification(title, {
                body,
                icon: '/favicon.ico',
                badge: '/favicon.ico',
                tag: `price-target-${transactionHash}`,
                requireInteraction: true, // Always require interaction for trade executions
                actions: [
                    {
                        action: 'view-transaction',
                        title: 'View Transaction'
                    },
                    {
                        action: 'view-portfolio',
                        title: 'View Portfolio'
                    }
                ],
                data: {
                    category: 'trading',
                    type: 'price_target_executed',
                    targetType,
                    tokenAddress,
                    tokenSymbol,
                    targetPrice,
                    executionPrice,
                    amount,
                    transactionHash,
                    gasFee,
                    timestamp: Date.now()
                }
            });

            notification.onclick = () => {
                window.focus();
                // Navigate to portfolio or trading page
                window.location.href = `/portfolio?highlight=${tokenAddress}`;
                notification.close();
            };

            // Handle notification actions
            if ('serviceWorker' in navigator) {
                navigator.serviceWorker.addEventListener('notificationclick', (event) => {
                    if (event.notification.data?.type === 'price_target_executed') {
                        event.notification.close();
                        
                        if (event.action === 'view-transaction') {
                            window.open(`https://solscan.io/tx/${transactionHash}`, '_blank');
                        } else if (event.action === 'view-portfolio') {
                            window.focus();
                            window.location.href = `/portfolio?highlight=${tokenAddress}`;
                        }
                    }
                });
            }

            // Don't auto-close trade execution notifications
            return notification;

        } catch (error) {
            console.error('Error sending price target execution notification:', error);
            return null;
        }
    }

    // Batch trending token notifications
    sendBatchTrendingAlert(alertData) {
        if (!this.canSendNotifications() || !this.shouldSendNotification('trending', 'batch_trending_alert')) {
            return null;
        }

        const { trendingTokens, count } = alertData;
        const topTokens = trendingTokens.slice(0, 3);
        const tokenNames = topTokens.map(t => t.symbol || t.name).join(', ');

        const title = `🔥 ${count} Tokens Trending`;
        const body = count > 3 
            ? `${tokenNames} and ${count - 3} more tokens are trending`
            : `${tokenNames} are trending`;

        try {
            const notification = new Notification(title, {
                body,
                icon: '/favicon.ico',
                badge: '/favicon.ico',
                tag: `batch-trending-${Date.now()}`,
                requireInteraction: false,
                data: {
                    category: 'trending',
                    type: 'batch_trending_alert',
                    trendingTokens,
                    count,
                    timestamp: Date.now()
                }
            });

            notification.onclick = () => {
                window.focus();
                window.location.href = '/?tab=trending';
                notification.close();
            };

            setTimeout(() => {
                notification.close();
            }, 12000);

            return notification;

        } catch (error) {
            console.error('Error sending batch trending notification:', error);
            return null;
        }
    }

    sendTradingNotification(type, data) {
        if (!this.canSendNotifications()) {
            return null;
        }

        let title = '';
        let body = '';

        switch (type) {
            case 'trade_executed':
                title = `✅ Trade Executed`;
                body = `${data.type} ${data.amount} ${data.tokenSymbol} at $${data.price}`;
                break;
            case 'bot_action':
                title = `🤖 Bot Action`;
                body = `${data.action} for ${data.tokenSymbol}: ${data.reason}`;
                break;
            case 'portfolio_update':
                title = `💼 Portfolio Update`;
                body = `Portfolio value: $${data.totalValue} (${data.change > 0 ? '+' : ''}${data.change}%)`;
                break;
            default:
                title = '🔔 Trading Notification';
                body = data.message || 'Trading activity update';
        }

        try {
            const notification = new Notification(title, {
                body,
                icon: '/favicon.ico',
                tag: `trading-${type}-${Date.now()}`,
                data: { type, ...data, timestamp: Date.now() }
            });

            notification.onclick = () => {
                window.focus();
                notification.close();
            };

            setTimeout(() => {
                notification.close();
            }, 8000);

            return notification;

        } catch (error) {
            console.error('Error sending trading notification:', error);
            return null;
        }
    }

    // Test notification
    sendTestNotification() {
        if (!this.canSendNotifications()) {
            console.warn('Cannot send test notification - permission not granted');
            return false;
        }

        try {
            const notification = new Notification('🧪 Test Notification', {
                body: 'Price alerts are working correctly!',
                icon: '/favicon.ico',
                tag: 'test-notification'
            });

            notification.onclick = () => {
                window.focus();
                notification.close();
            };

            setTimeout(() => {
                notification.close();
            }, 5000);

            return true;

        } catch (error) {
            console.error('Error sending test notification:', error);
            return false;
        }
    }

    // Investment-related notifications
    sendInvestmentNotification(type, data) {
        if (!this.canSendNotifications() || !this.shouldSendNotification('investment', type)) {
            return null;
        }

        let title = '';
        let body = '';
        let icon = '/favicon.ico';

        switch (type) {
            case 'investment_confirmed':
                title = `✅ Investment Confirmed`;
                body = `Successfully invested ${data.amount} SOL in ${data.poolName}`;
                break;
            case 'withdrawal_requested':
                title = `📤 Withdrawal Requested`;
                body = `Withdrawal request for ${data.amount} SOL from ${data.poolName} submitted`;
                break;
            case 'withdrawal_approved':
                title = `✅ Withdrawal Approved`;
                body = `Your withdrawal of ${data.amount} SOL from ${data.poolName} has been approved`;
                break;
            case 'withdrawal_completed':
                title = `💰 Withdrawal Completed`;
                body = `${data.amount} SOL has been transferred to your wallet from ${data.poolName}`;
                break;
            case 'performance_gain':
                title = `📈 Significant Gain`;
                body = `Your investment in ${data.poolName} is up ${data.percentage}% (${data.amount} SOL)`;
                break;
            case 'performance_loss':
                title = `📉 Significant Loss`;
                body = `Your investment in ${data.poolName} is down ${data.percentage}% (${data.amount} SOL)`;
                break;
            case 'pool_trade_large':
                title = `🔄 Large Pool Trade`;
                body = `${data.poolName} executed a ${data.tradeType} trade worth ${data.amount} SOL`;
                break;
            case 'pool_strategy_change':
                title = `⚙️ Strategy Update`;
                body = `${data.poolName} strategy has been updated: ${data.newStrategy}`;
                break;
            case 'pool_status_change':
                title = `🏦 Pool Status Change`;
                body = `${data.poolName} status changed to ${data.newStatus}`;
                break;
            case 'fee_collected':
                title = `💳 Fee Collected`;
                body = `Management fee of ${data.amount} SOL collected from ${data.poolName}`;
                break;
            default:
                title = '🔔 Investment Notification';
                body = data.message || 'Investment activity update';
        }

        try {
            const notification = new Notification(title, {
                body,
                icon,
                badge: icon,
                tag: `investment-${type}-${Date.now()}`,
                requireInteraction: ['withdrawal_completed', 'investment_confirmed'].includes(type),
                data: {
                    type,
                    category: 'investment',
                    ...data,
                    timestamp: Date.now()
                }
            });

            notification.onclick = () => {
                window.focus();
                
                // Navigate to appropriate page based on notification type
                if (type.includes('withdrawal') || type.includes('investment')) {
                    window.location.href = '/investments';
                } else if (type.includes('pool')) {
                    window.location.href = `/pool/${data.poolId}`;
                }
                
                notification.close();
            };

            // Auto-close based on importance
            const autoCloseTime = ['withdrawal_completed', 'investment_confirmed'].includes(type) ? 15000 : 10000;
            setTimeout(() => {
                notification.close();
            }, autoCloseTime);

            return notification;

        } catch (error) {
            console.error('Error sending investment notification:', error);
            return null;
        }
    }

    sendPerformanceAlert(alertData) {
        if (!this.canSendNotifications() || !this.shouldSendNotification('performance', alertData.type)) {
            return null;
        }

        const {
            type,
            poolName,
            currentValue,
            previousValue,
            percentageChange,
            timeframe,
            threshold
        } = alertData;

        let title = '';
        let body = '';
        let requireInteraction = false;

        if (type === 'significant_gain') {
            title = `🚀 Significant Gain Alert`;
            body = `${poolName}: +${percentageChange.toFixed(2)}% (${currentValue.toFixed(3)} SOL) in ${timeframe}`;
            requireInteraction = Math.abs(percentageChange) > 20; // Require interaction for >20% changes
        } else if (type === 'significant_loss') {
            title = `⚠️ Significant Loss Alert`;
            body = `${poolName}: ${percentageChange.toFixed(2)}% (${currentValue.toFixed(3)} SOL) in ${timeframe}`;
            requireInteraction = Math.abs(percentageChange) > 15; // Require interaction for >15% losses
        } else if (type === 'milestone_reached') {
            title = `🎯 Milestone Reached`;
            body = `${poolName} reached ${alertData.milestone} (${currentValue.toFixed(3)} SOL)`;
        }

        try {
            const notification = new Notification(title, {
                body,
                icon: '/favicon.ico',
                badge: '/favicon.ico',
                tag: `performance-${type}-${alertData.poolId}`,
                requireInteraction,
                data: {
                    category: 'performance',
                    ...alertData,
                    timestamp: Date.now()
                }
            });

            notification.onclick = () => {
                window.focus();
                window.location.href = `/investments?pool=${alertData.poolId}`;
                notification.close();
            };

            setTimeout(() => {
                notification.close();
            }, requireInteraction ? 20000 : 12000);

            return notification;

        } catch (error) {
            console.error('Error sending performance alert:', error);
            return null;
        }
    }

    sendPoolActivityNotification(activityData) {
        if (!this.canSendNotifications() || !this.shouldSendNotification('pool_activity', activityData.type)) {
            return null;
        }

        const {
            type,
            poolName,
            poolId,
            managerName,
            tradeAmount,
            tokenSymbol,
            strategyReason
        } = activityData;

        let title = '';
        let body = '';

        switch (type) {
            case 'large_trade':
                title = `💼 Large Trade Executed`;
                body = `${poolName}: ${activityData.tradeType} ${tradeAmount} ${tokenSymbol || 'SOL'}`;
                if (strategyReason) {
                    body += ` - ${strategyReason}`;
                }
                break;
            case 'strategy_change':
                title = `📋 Strategy Updated`;
                body = `${managerName} updated ${poolName} strategy`;
                break;
            case 'new_position':
                title = `🎯 New Position`;
                body = `${poolName} opened new position in ${tokenSymbol}`;
                break;
            case 'position_closed':
                title = `🔚 Position Closed`;
                body = `${poolName} closed position in ${tokenSymbol}`;
                break;
            case 'risk_alert':
                title = `⚠️ Risk Alert`;
                body = `${poolName}: ${activityData.riskMessage}`;
                break;
        }

        try {
            const notification = new Notification(title, {
                body,
                icon: '/favicon.ico',
                tag: `pool-activity-${type}-${poolId}`,
                requireInteraction: type === 'risk_alert',
                data: {
                    category: 'pool_activity',
                    ...activityData,
                    timestamp: Date.now()
                }
            });

            notification.onclick = () => {
                window.focus();
                window.location.href = `/pool/${poolId}`;
                notification.close();
            };

            setTimeout(() => {
                notification.close();
            }, type === 'risk_alert' ? 15000 : 10000);

            return notification;

        } catch (error) {
            console.error('Error sending pool activity notification:', error);
            return null;
        }
    }

    // Notification preferences management
    loadPreferences() {
        try {
            const saved = localStorage.getItem('notificationPreferences');
            return saved ? JSON.parse(saved) : this.getDefaultPreferences();
        } catch (error) {
            console.error('Error loading notification preferences:', error);
            return this.getDefaultPreferences();
        }
    }

    getDefaultPreferences() {
        return {
            enabled: true,
            categories: {
                price_alerts: {
                    enabled: true,
                    frequency: 'all' // 'all', 'important', 'none'
                },
                trading: {
                    enabled: true,
                    frequency: 'important'
                },
                investment: {
                    enabled: true,
                    frequency: 'all'
                },
                performance: {
                    enabled: true,
                    frequency: 'important',
                    thresholds: {
                        gain: 10, // Notify for gains > 10%
                        loss: 5   // Notify for losses > 5%
                    }
                },
                pool_activity: {
                    enabled: true,
                    frequency: 'important',
                    largeTradeThreshold: 10 // SOL
                },
                withdrawal: {
                    enabled: true,
                    frequency: 'all'
                },
                trending: {
                    enabled: true,
                    frequency: 'important',
                    minTrendScore: 60, // Minimum trend score to notify
                    maxAlertsPerHour: 5, // Maximum trending alerts per hour
                    preferredSources: ['dexscreener', 'on_chain'], // Preferred detection sources
                    blacklistedTokens: [] // Tokens to never alert about
                }
            },
            quietHours: {
                enabled: false,
                start: '22:00',
                end: '08:00'
            },
            email: {
                enabled: false,
                criticalOnly: true
            },
            pushNotifications: {
                enabled: false,
                endpoint: null,
                keys: null
            }
        };
    }

    savePreferences(preferences) {
        try {
            this.preferences = { ...this.preferences, ...preferences };
            localStorage.setItem('notificationPreferences', JSON.stringify(this.preferences));
            return true;
        } catch (error) {
            console.error('Error saving notification preferences:', error);
            return false;
        }
    }

    updatePreferences(updates) {
        const newPreferences = { ...this.preferences, ...updates };
        return this.savePreferences(newPreferences);
    }

    shouldSendNotification(category, type) {
        if (!this.preferences.enabled) {
            return false;
        }

        const categoryPrefs = this.preferences.categories[category];
        if (!categoryPrefs || !categoryPrefs.enabled) {
            return false;
        }

        // Check quiet hours
        if (this.preferences.quietHours.enabled && this.isQuietHours()) {
            // Only send critical notifications during quiet hours
            const criticalTypes = [
                'withdrawal_completed',
                'investment_confirmed',
                'significant_loss',
                'risk_alert'
            ];
            if (!criticalTypes.includes(type)) {
                return false;
            }
        }

        // Check frequency settings
        if (categoryPrefs.frequency === 'none') {
            return false;
        }

        if (categoryPrefs.frequency === 'important') {
            const importantTypes = [
                'price_above', 'price_below', // price alerts
                'trade_executed', 'bot_action', 'price_target_executed', // trading
                'investment_confirmed', 'withdrawal_completed', 'performance_gain', 'performance_loss', // investment
                'significant_gain', 'significant_loss', 'milestone_reached', // performance
                'large_trade', 'strategy_change', 'risk_alert', // pool activity
                'withdrawal_approved', 'withdrawal_completed', // withdrawal
                'trending_token_alert' // trending (batch alerts are not considered important)
            ];
            return importantTypes.includes(type);
        }

        return true; // 'all' frequency
    }

    isQuietHours() {
        if (!this.preferences.quietHours.enabled) {
            return false;
        }

        const now = new Date();
        const currentTime = now.getHours() * 100 + now.getMinutes();
        
        const start = this.parseTime(this.preferences.quietHours.start);
        const end = this.parseTime(this.preferences.quietHours.end);

        if (start <= end) {
            return currentTime >= start && currentTime <= end;
        } else {
            // Quiet hours span midnight
            return currentTime >= start || currentTime <= end;
        }
    }

    parseTime(timeString) {
        const [hours, minutes] = timeString.split(':').map(Number);
        return hours * 100 + minutes;
    }

    getPreferences() {
        return { ...this.preferences };
    }

    // Test notifications with different types
    sendTestNotification(type = 'basic') {
        if (!this.canSendNotifications()) {
            console.warn('Cannot send test notification - permission not granted');
            return false;
        }

        const testData = {
            basic: {
                title: '🧪 Test Notification',
                body: 'Basic notification test - everything is working!',
                data: { type: 'test', category: 'system' }
            },
            investment: {
                title: '💰 Investment Test',
                body: 'Test investment notification - 5.0 SOL invested in Test Pool',
                data: { type: 'investment_confirmed', category: 'investment', poolName: 'Test Pool', amount: 5.0 }
            },
            performance: {
                title: '📈 Performance Test',
                body: 'Test performance alert - Test Pool up 15.5% today',
                data: { type: 'significant_gain', category: 'performance', poolName: 'Test Pool', percentage: 15.5 }
            },
            pool_activity: {
                title: '🔄 Pool Activity Test',
                body: 'Test pool activity - Large trade executed in Test Pool',
                data: { type: 'large_trade', category: 'pool_activity', poolName: 'Test Pool' }
            }
        };

        const testConfig = testData[type] || testData.basic;

        try {
            const notification = new Notification(testConfig.title, {
                body: testConfig.body,
                icon: '/favicon.ico',
                tag: `test-notification-${type}`,
                data: testConfig.data
            });

            notification.onclick = () => {
                window.focus();
                notification.close();
            };

            setTimeout(() => {
                notification.close();
            }, 5000);

            return true;

        } catch (error) {
            console.error('Error sending test notification:', error);
            return false;
        }
    }

    // Push notification registration for mobile devices
    async registerPushNotifications() {
        if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
            console.warn('Push notifications are not supported');
            return { success: false, error: 'Push notifications not supported' };
        }

        try {
            // Register service worker if not already registered
            let registration = await navigator.serviceWorker.getRegistration();
            if (!registration) {
                registration = await navigator.serviceWorker.register('/sw.js');
                console.log('Service worker registered');
            }

            // Check if already subscribed
            let subscription = await registration.pushManager.getSubscription();
            
            if (!subscription) {
                // Subscribe to push notifications
                const vapidPublicKey = process.env.REACT_APP_VAPID_PUBLIC_KEY || 'your-vapid-public-key';
                
                subscription = await registration.pushManager.subscribe({
                    userVisibleOnly: true,
                    applicationServerKey: this.urlBase64ToUint8Array(vapidPublicKey)
                });

                console.log('Push notification subscription created');
            }

            // Send subscription to server
            const subscriptionData = {
                endpoint: subscription.endpoint,
                keys: {
                    p256dh: btoa(String.fromCharCode.apply(null, new Uint8Array(subscription.getKey('p256dh')))),
                    auth: btoa(String.fromCharCode.apply(null, new Uint8Array(subscription.getKey('auth'))))
                }
            };

            const response = await fetch('/api/notifications/push-subscription', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify(subscriptionData)
            });

            if (response.ok) {
                // Update preferences
                this.updatePreferences({
                    pushNotifications: {
                        enabled: true,
                        endpoint: subscription.endpoint,
                        keys: subscriptionData.keys
                    }
                });

                return { success: true, subscription: subscriptionData };
            } else {
                throw new Error('Failed to register push subscription with server');
            }

        } catch (error) {
            console.error('Error registering push notifications:', error);
            return { success: false, error: error.message };
        }
    }

    async unregisterPushNotifications() {
        try {
            const registration = await navigator.serviceWorker.getRegistration();
            if (registration) {
                const subscription = await registration.pushManager.getSubscription();
                if (subscription) {
                    await subscription.unsubscribe();
                    
                    // Remove from server
                    await fetch('/api/notifications/push-subscription', {
                        method: 'DELETE',
                        headers: {
                            'Authorization': `Bearer ${localStorage.getItem('token')}`
                        }
                    });

                    // Update preferences
                    this.updatePreferences({
                        pushNotifications: {
                            enabled: false,
                            endpoint: null,
                            keys: null
                        }
                    });

                    console.log('Push notifications unregistered');
                    return { success: true };
                }
            }

            return { success: true, message: 'No active subscription found' };

        } catch (error) {
            console.error('Error unregistering push notifications:', error);
            return { success: false, error: error.message };
        }
    }

    urlBase64ToUint8Array(base64String) {
        const padding = '='.repeat((4 - base64String.length % 4) % 4);
        const base64 = (base64String + padding)
            .replace(/-/g, '+')
            .replace(/_/g, '/');

        const rawData = window.atob(base64);
        const outputArray = new Uint8Array(rawData.length);

        for (let i = 0; i < rawData.length; ++i) {
            outputArray[i] = rawData.charCodeAt(i);
        }
        return outputArray;
    }

    // Enhanced notification handling for WebSocket messages
    handleWebSocketNotification(notificationData) {
        const { type, data } = notificationData;

        switch (type) {
            case 'trending_token_alert':
                this.sendTrendingTokenAlert(data);
                break;
            case 'price_target_executed':
                this.sendPriceTargetExecutionNotification(data);
                break;
            case 'batch_trending_alert':
                this.sendBatchTrendingAlert(data);
                break;
            case 'price_alert':
                this.sendPriceAlert(data);
                break;
            case 'investment_confirmed':
            case 'withdrawal_completed':
            case 'performance_gain':
            case 'performance_loss':
                this.sendInvestmentNotification(type, data);
                break;
            case 'significant_gain':
            case 'significant_loss':
                this.sendPerformanceAlert(data);
                break;
            case 'large_trade':
            case 'strategy_change':
            case 'risk_alert':
                this.sendPoolActivityNotification(data);
                break;
            default:
                console.log('Unknown notification type:', type);
        }
    }

    getPermissionStatus() {
        return {
            isSupported: this.isSupported,
            permission: this.permission,
            canSend: this.canSendNotifications(),
            preferences: this.preferences,
            pushSupported: 'serviceWorker' in navigator && 'PushManager' in window,
            pushEnabled: this.preferences.pushNotifications?.enabled || false
        };
    }
}

// Create singleton instance
const notificationService = new NotificationService();

export default notificationService;